
import { useState } from 'react';
import { getOrCreateVisitorId } from '@/utils/cookieUtils';
import { sendMessageToOpenAI } from '@/services/openaiService';
import { triggerNotionCloneWebhook } from '@/services/notion/webhookService';

export const useWebhookTrigger = () => {
  const [isWebhookTriggering, setIsWebhookTriggering] = useState(false);
  const [lastTriggerTime, setLastTriggerTime] = useState(0);
  const [webhookResponse, setWebhookResponse] = useState<any>(null);

  const extractBusinessInsights = async (context: string) => {
    console.log('Extracting business insights from conversation...');
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY || '';
    
    if (!apiKey) {
      console.error('OpenAI API key not found');
      return [];
    }
    
    try {
      const messages = [
        {
          role: 'system',
          content: `Extract exactly 3 valuable business insights from the conversation. 
          Each insight should be something explicitly stated or confirmed by the user about their business needs. 
          Focus on unique selling points, target audience, or business challenges. 
          Return ONLY an array of 3 distinct insights in JSON format like this: ["insight 1", "insight 2", "insight 3"]
          Do not include explanations, just the array of insights.`
        },
        {
          role: 'user',
          content: context
        }
      ];
      
      const insightsText = await sendMessageToOpenAI(messages, apiKey);
      
      try {
        // Try to parse the response as JSON
        const insights = JSON.parse(insightsText);
        
        if (Array.isArray(insights) && insights.length > 0) {
          console.log('Extracted business insights:', insights);
          return insights.slice(0, 3); // Ensure we only return max 3 insights
        }
      } catch (parseError) {
        console.error('Failed to parse insights as JSON:', parseError);
        
        // If parsing fails, try to extract insights using regex
        const insightMatches = insightsText.match(/"([^"]+)"/g);
        if (insightMatches && insightMatches.length > 0) {
          const cleanedInsights = insightMatches
            .map(match => match.replace(/"/g, ''))
            .slice(0, 3);
          console.log('Extracted insights using regex:', cleanedInsights);
          return cleanedInsights;
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error extracting business insights:', error);
      return [];
    }
  };

  const triggerWebhook = async (context: string) => {
    const now = Date.now();
    const TRIGGER_INTERVAL = 60000; // 1 minute between triggers

    // Prevent multiple triggers within a short time frame
    if (isWebhookTriggering || (now - lastTriggerTime < TRIGGER_INTERVAL)) {
      console.log('Skipping webhook trigger: Too soon or already triggering', {
        isWebhookTriggering,
        timeSinceLastTrigger: now - lastTriggerTime,
        minimumInterval: TRIGGER_INTERVAL
      });
      return null;
    }

    try {
      setIsWebhookTriggering(true);
      const userId = getOrCreateVisitorId();
      
      // Extract business insights before triggering webhook
      const businessInsights = await extractBusinessInsights(context);
      
      // Enhance context with business insights
      let enhancedContext = context;
      if (businessInsights.length > 0) {
        enhancedContext += '\n\n--- KEY BUSINESS INSIGHTS ---\n';
        businessInsights.forEach((insight, index) => {
          enhancedContext += `${index + 1}. ${insight}\n`;
        });
      }
      
      // Use the triggerNotionCloneWebhook function to call the webhook
      const notionUrl = await triggerNotionCloneWebhook(userId, enhancedContext);
      
      if (notionUrl) {
        console.log('Received Notion URL from webhook:', notionUrl);
        
        // Add a new AI message to announce completion
        const event = new CustomEvent('ai-message', {
          detail: {
            message: `✨ Great news! I've created your custom Notion business plan. You can access it here: <a href="${notionUrl}" target="_blank" rel="noopener noreferrer" class="text-goldcore underline">View your Notion plan</a>\n\nTo make the most of this plan and get personalized guidance, I'd love to offer you a free consultation. Would you like to provide your contact details so we can schedule that?`
          }
        });
        window.dispatchEvent(event);
        
        setLastTriggerTime(now);
        setIsWebhookTriggering(false);
        return notionUrl;
      }
      
      // Fallback to the local API endpoint if the direct webhook doesn't return a URL
      try {
        console.log('Trying local API endpoint as fallback...');
        
        const response = await fetch('/api/test?endpoint=clone-notion-page', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            user_id: userId,
            parent_id: '1d1e21e1f5118092995cf40a3ecf1890',
            context: enhancedContext
          })
        });
        
        if (response.ok) {
          try {
            const data = await response.json();
            console.log('API response data:', data);
            setWebhookResponse(data);
            
            if (data && data.page && data.page.url) {
              const url = data.page.url;
              console.log('Returning URL from API:', url);
              
              // Add a new AI message to announce completion
              const event = new CustomEvent('ai-message', {
                detail: {
                  message: `✨ Great news! I've created your custom Notion business plan. You can access it here: <a href="${url}" target="_blank" rel="noopener noreferrer" class="text-goldcore underline">View your Notion plan</a>\n\nTo make the most of this plan and get personalized guidance, I'd love to offer you a free consultation. Would you like to provide your contact details so we can schedule that?`
                }
              });
              window.dispatchEvent(event);
              
              setLastTriggerTime(now);
              setIsWebhookTriggering(false);
              return url;
            }
          } catch (error) {
            console.log('Could not parse API response as JSON:', error);
          }
        } else {
          console.error('API request failed with status:', response.status);
          const errorText = await response.text();
          console.error('API error response:', errorText);
        }
      } catch (error) {
        console.error('Error calling API endpoint:', error);
      }
      
      setLastTriggerTime(now);
      return null;
    } catch (error) {
      console.error('Error triggering webhook:', error);
      return null;
    } finally {
      setIsWebhookTriggering(false);
    }
  };

  return {
    triggerWebhook,
    isWebhookTriggering,
    webhookResponse
  };
};
